import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ArrowLeft, Box } from "lucide-react";
import { API, setSession } from "../../lib/auth";

export default function TenantLogin() {
  const { subdomain } = useParams();
  const nav = useNavigate();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.email.includes("@") || form.password.length < 6) return toast.error("Enter valid credentials");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { ...form, subdomain });
      setSession({ token: data.token, user: data.user, tenant: data.tenant });
      toast.success(`Welcome back to ${data.tenant.company_name}`);
      nav(`/t/${subdomain}/dashboard`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="tenant-login-page" className="min-h-screen bg-[#FFFCF2] grid lg:grid-cols-2">
      <div className="hidden lg:flex relative overflow-hidden bg-gradient-to-br from-[#1B4332] via-[#2D6A4F] to-[#52B788] text-white p-12 flex-col justify-between">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
        <Link to="/" className="relative inline-flex items-center gap-2 text-white/80 hover:text-white text-sm">
          <ArrowLeft size={16} /> Back to brivopos.com
        </Link>
        <div className="relative">
          <div className="w-16 h-16 rounded-2xl bg-white/15 flex items-center justify-center mb-7"><Box size={32} /></div>
          <h1 className="font-display text-5xl font-extrabold tracking-tight leading-[1.05]">Welcome back to your workspace.</h1>
          <p className="mt-5 text-white/80 text-lg max-w-md">Log into <span className="font-semibold">{subdomain}.brivopos.com</span> to manage your business.</p>
        </div>
        <div className="text-xs text-white/50">© Brivo Inc. 2026</div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-14">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#1B4332] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M4 6h12a4 4 0 010 8H4V6z" stroke="#E8F5E9" strokeWidth="2.5" strokeLinejoin="round"/>
                <path d="M4 14h14a4 4 0 010 8H4v-8z" stroke="#E8F5E9" strokeWidth="2.5" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="font-display font-extrabold text-2xl text-black">Brivo360</span>
          </div>
          <h2 className="font-display text-3xl md:text-4xl font-extrabold text-black tracking-tight">Log in</h2>
          <p className="mt-2 text-black/60">Workspace: <span className="font-mono text-black font-semibold">{subdomain}</span></p>
          <form onSubmit={onSubmit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Email</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Mail size={16} className="text-black/40" />
                <input data-testid="tenant-login-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="flex-1 px-3 py-3 focus:outline-none" placeholder="you@business.com" />
              </div>
            </div>
            <div>
              <label className="text-sm font-semibold text-black mb-1.5 block">Password</label>
              <div className="flex items-center rounded-xl border border-black/15 focus-within:border-[#1B4332] bg-white px-4">
                <Lock size={16} className="text-black/40" />
                <input data-testid="tenant-login-password" type={show ? "text" : "password"} value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="flex-1 px-3 py-3 focus:outline-none" placeholder="••••••••" />
                <button type="button" onClick={() => setShow(!show)} className="text-black/40">
                  {show ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>
            </div>
            <button data-testid="tenant-login-submit" type="submit" disabled={loading}
              className="w-full mt-2 py-3.5 rounded-xl bg-gradient-to-r from-[#1FA868] to-[#2D6A4F] text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
              {loading ? "Logging in..." : "Log in"} <ArrowRight size={16} />
            </button>
          </form>
          <p className="mt-5 text-sm text-center text-black/60">
            Need a workspace? <Link to="/register" className="text-[#1B4332] font-semibold">Create one</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
